/**
 * usePayment Hook
 * 결제 관련 로직을 처리하는 React Hook
 */

import { useState, useCallback, useMemo } from 'react';
import type { PaymentRequest } from '../../../domain/models/payments/Payment';
import { RequestPaymentUseCase } from '../../../domain/usecases/payments/RequestPaymentUseCase';
import { GenerateOrderIdUseCase } from '../../../domain/usecases/payments/generateOrderIdUseCase';
import { inicisPaymentRepository } from '../../../data/repositories/InicisPaymentRepositoryImpl';
import { ApprovePaymentFromSearchParamsUseCase } from '../../../domain/usecases/payments/ApprovePaymentFromSearchParamsUseCase';

/**
 * 결제 Hook
 */
export const usePayment = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Use Cases 초기화
  const requestPaymentUseCase = useMemo(
    () => new RequestPaymentUseCase(inicisPaymentRepository),
    []
  );
  const generateOrderIdUseCase = useMemo(
    () => new GenerateOrderIdUseCase(inicisPaymentRepository),
    []
  );

  const approvePaymentFromSearchParamsUseCase = useMemo(
    () => new ApprovePaymentFromSearchParamsUseCase(inicisPaymentRepository),
    []
  );

  /**
   * 결제 요청
   */
  const requestPayment = useCallback(
    async (request: PaymentRequest) => {
      setIsLoading(true);
      setError(null);

      try {
        await requestPaymentUseCase.execute(request);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('결제 요청 중 오류가 발생했습니다.');
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [requestPaymentUseCase]
  );

  /**
   * 주문번호 생성
   */
  const generateOrderId = useCallback(
    async (productId: string): Promise<string> => {
      return generateOrderIdUseCase.execute(productId);
    },
    [generateOrderIdUseCase]
  );

  /**
   * 결제 승인
   */
  const approvePaymentFromSearchParams = useCallback(
    async (searchParams: URLSearchParams): Promise<boolean> => {
      setIsLoading(true);
      setError(null);
      try {
        return await approvePaymentFromSearchParamsUseCase.execute(searchParams);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('결제 승인 중 오류가 발생했습니다.');
        setError(error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [approvePaymentFromSearchParamsUseCase]
  );

  return {
    isLoading,
    error,
    requestPayment,
    generateOrderId,
    approvePaymentFromSearchParams
  };
};
